import React, { useState } from 'react';
import { AlertCircle, LogIn } from 'lucide-react';
import axios from 'axios';

import { Button } from '../ui/button';
import { Input } from '../ui/input';

export const LoginForm = ({
  BACKEND_URL,
  handleLogin,
  showNotification,
  setResetEmail,
  setResetToken,
  setResetPasswordDraft,
  setResetPasswordConfirm,
  setResetMessage,
  setShowResetPassword,
  setShowRegister,
  setShowLogin
}) => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');

  const handleSubmit = async () => {
    setLoginError('');
    setResendMessage('');

    try {
      setIsLoading(true);
      const result = await handleLogin(formData.email, formData.password);
      if (!result.ok) {
        setLoginError(result.error || 'No se pudo iniciar sesión');
      } else {
        showNotification('¡Bienvenido!', 'Inicio de sesión exitoso', 'success');
      }
    } catch (error) {
      console.error('Error al iniciar sesión:', error);
      setLoginError(error.response?.data?.detail || 'No se pudo iniciar sesión. Verifica tus credenciales.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setResendMessage('Ingresa tu email para reenviar la verificación.');
      return;
    }
    try {
      setResendLoading(true);
      setResendMessage('');
      const response = await axios.post(`${BACKEND_URL}/api/auth/resend-verification-public`, {
        email: formData.email
      });
      setResendMessage(response.data?.message || 'Si el correo existe, enviaremos un enlace de verificación.');
    } catch (error) {
      setResendMessage(error.response?.data?.detail || 'No se pudo reenviar el correo. Inténtalo más tarde.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="space-y-6 p-2">
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-300">Email</label>
          <Input
            type="email"
            placeholder="tucorreo@ejemplo.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="bg-white/5 border-white/20 text-white placeholder-gray-400 h-12 rounded-xl focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-300">Contraseña</label>
          <Input
            type="password"
            placeholder="Tu contraseña"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className="bg-white/5 border-white/20 text-white placeholder-gray-400 h-12 rounded-xl focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
          />
        </div>
      </div>

      {loginError && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-500/20 rounded-full flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-red-400" />
            </div>
            <div>
              <p className="text-red-400 font-medium text-sm">Error de acceso</p>
              <p className="text-red-300 text-xs mt-1">{loginError}</p>
            </div>
          </div>
          {loginError.toLowerCase().includes('verificado') && (
            <div className="mt-3">
              <Button
                type="button"
                variant="outline"
                className="border-gray-700 text-amber-300 hover:bg-gray-800"
                onClick={handleResendVerification}
                disabled={resendLoading}
              >
                {resendLoading ? 'Reenviando...' : 'Reenviar verificación'}
              </Button>
            </div>
          )}
          {resendMessage && (
            <p className="text-xs text-amber-200 mt-2">{resendMessage}</p>
          )}
        </div>
      )}

      <Button
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full h-12 bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-700 hover:to-yellow-700 text-white font-semibold rounded-xl shadow-lg disabled:opacity-50"
      >
        {isLoading ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            Iniciando...
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <LogIn className="w-4 h-4" />
            Iniciar Sesión
          </div>
        )}
      </Button>

      <div className="flex items-center justify-between text-xs text-gray-400">
        <button
          type="button"
          className="text-amber-400 hover:text-amber-300"
          onClick={() => {
            setResetEmail(formData.email || '');
            setResetToken('');
            setResetPasswordDraft('');
            setResetPasswordConfirm('');
            setResetMessage('');
            setShowResetPassword(true);
          }}
        >
          ¿Olvidaste tu contraseña?
        </button>
        <span>
          ¿No tienes cuenta?
          <button
            type="button"
            onClick={() => {
              setShowLogin(false);
              setShowRegister(true);
            }}
            className="text-amber-400 cursor-pointer hover:text-amber-300 ml-1"
          >
            Regístrate aquí
          </button>
        </span>
      </div>
    </div>
  );
};
